import React, { useState, useEffect } from 'react';
import { BarChart, Bar, PieChart, Pie, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Download, Filter, TrendingUp, Users, Clock, AlertCircle } from 'lucide-react';
import { format, subDays, startOfMonth, endOfMonth } from 'date-fns';
import { User } from '../../types';
import { toast } from '../../utils/toast';
import { readApiError } from '../common/apiError';

interface AdvancedReportMetrics {
  totalTasks: number;
  completedTasks: number;
  inProgressTasks: number;
  overdueTasks: number;
  averageCompletionTime: number;
  byPriority: { [key: string]: number };
  byStatus: { [key: string]: number };
  byCategory: { [key: string]: number };
  byAssignee: { [key: string]: number };
  estimatedVsActual: {
    totalEstimated: number;
    totalActual: number;
    variance: number;
  };
}

interface AdvancedReportsProps {
  currentUser?: User;
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const PRIORITY_COLORS = {
  low: '#10b981',
  medium: '#f59e0b',
  high: '#f97316',
  critical: '#ef4444'
};

const AdvancedReports: React.FC<AdvancedReportsProps> = ({ currentUser }) => {
  const [metrics, setMetrics] = useState<AdvancedReportMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: format(startOfMonth(new Date()), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(new Date()), 'yyyy-MM-dd')
  });
  const [filters, setFilters] = useState({
    assigneeId: '',
    priority: '',
    status: ''
  });
  const [assignableUsers, setAssignableUsers] = useState<{ id: string; name: string }[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    fetchReportData();
  }, [dateRange, filters, currentUser]);

  useEffect(() => {
    // Populates the Assignee filter dropdown — filters.assigneeId was already
    // being sent to the backend, but nothing in the UI let a user set it.
    fetch('/api/users')
      .then(res => res.ok ? res.json() : [])
      .then(users => setAssignableUsers((Array.isArray(users) ? users : []).map((u: any) => ({ id: u.id, name: u.name || u.username || u.email }))))
      .catch(() => setAssignableUsers([]));
  }, []);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (dateRange.startDate) params.append('startDate', dateRange.startDate);
      if (dateRange.endDate) params.append('endDate', dateRange.endDate);
      if (filters.assigneeId) params.append('assigneeId', filters.assigneeId);
      if (filters.priority) params.append('priority', filters.priority);
      if (filters.status) params.append('status', filters.status);
      
      // Add user context for filtering
      if (currentUser) {
        params.append('userId', currentUser.id);
        params.append('userRole', currentUser.role);
      }

      const response = await fetch(`/api/tasks/reports/advanced?${params}`);
      if (!response.ok) {
        const message = await readApiError(response, 'Failed to load report');
        setLoadError(message);
        toast(message, 'error');
        return;
      }
      const data = await response.json();
      setMetrics(data);
      setLoadError(null);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      setLoadError('Failed to load report');
      toast('Failed to load report', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!metrics) return;

    const csvContent = [
      ['Metric', 'Value'],
      ['Total Tasks', metrics.totalTasks],
      ['Completed Tasks', metrics.completedTasks],
      ['In Progress Tasks', metrics.inProgressTasks],
      ['Overdue Tasks', metrics.overdueTasks],
      ['Average Completion Time (hours)', metrics.averageCompletionTime],
      [''],
      ['Priority Distribution', ''],
      ...Object.entries(metrics.byPriority).map(([priority, count]) => [priority, count]),
      [''],
      ['Status Distribution', ''],
      ...Object.entries(metrics.byStatus).map(([status, count]) => [status, count]),
      [''],
      ['Assignee Workload', ''],
      ...Object.entries(metrics.byAssignee).map(([assignee, count]) => [assignee, count])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `task-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleQuickDateRange = (days: number) => {
    const end = new Date();
    const start = subDays(end, days);
    setDateRange({
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd')
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <p className="text-gray-600">{loadError || 'No data available'}</p>
      </div>
    );
  }

  // Transform data for charts
  const priorityData = Object.entries(metrics.byPriority).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value,
    color: PRIORITY_COLORS[name as keyof typeof PRIORITY_COLORS] || '#6b7280'
  }));

  const statusData = Object.entries(metrics.byStatus).map(([name, value]) => ({
    name: name.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    value
  }));

  const assigneeData = Object.entries(metrics.byAssignee)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([name, tasks]) => ({ name, tasks }));

  // The backend already computed byCategory but nothing rendered it — this
  // was dead data until now.
  const categoryData = Object.entries(metrics.byCategory || {}).map(([name, value]) => ({
    name,
    value
  }));

  const completionRate = metrics.totalTasks > 0 
    ? ((metrics.completedTasks / metrics.totalTasks) * 100).toFixed(1) 
    : '0';

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Advanced Reports</h1>
          <p className="text-gray-600 mt-1">Comprehensive task analytics and insights</p>
        </div>
        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Download size={20} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={20} className="text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="new">New</option>
              <option value="assigned">Assigned</option>
              <option value="in-progress">In Progress</option>
              <option value="testing">Testing</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assignee</label>
            <select
              value={filters.assigneeId}
              onChange={(e) => setFilters({ ...filters, assigneeId: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Assignees</option>
              {assignableUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quick Range</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleQuickDateRange(7)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                7d
              </button>
              <button
                onClick={() => handleQuickDateRange(30)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                30d
              </button>
              <button
                onClick={() => handleQuickDateRange(90)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                90d
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Tasks</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">{metrics.totalTasks}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <TrendingUp className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completion Rate</p>
              <p className="text-3xl font-bold text-green-600 mt-2">{completionRate}%</p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <TrendingUp className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Completion Time</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">{metrics.averageCompletionTime.toFixed(1)}h</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Clock className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Overdue Tasks</p>
              <p className="text-3xl font-bold text-red-600 mt-2">{metrics.overdueTasks}</p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <AlertCircle className="text-red-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Priority Distribution Pie Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={priorityData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {priorityData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status Distribution Bar Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category Distribution */}
      {categoryData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" name="Tasks">
                {categoryData.map((_entry, index) => (
                  <Cell key={`cat-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Assignee Workload Chart */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Users size={20} />
          Assignee Workload (Top 10)
        </h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={assigneeData} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" />
            <YAxis dataKey="name" type="category" width={150} />
            <Tooltip />
            <Legend />
            <Bar dataKey="tasks" fill="#10b981" name="Tasks Assigned" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Estimated vs Actual Hours */}
      {(metrics.estimatedVsActual.totalEstimated > 0 || metrics.estimatedVsActual.totalActual > 0) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Estimated vs Actual Hours</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Estimated Hours</p>
              <p className="text-4xl font-bold text-blue-600 mt-2">{metrics.estimatedVsActual.totalEstimated}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Actual Hours</p>
              <p className="text-4xl font-bold text-green-600 mt-2">{metrics.estimatedVsActual.totalActual}</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-600">Variance</p>
              <p className={`text-4xl font-bold mt-2 ${metrics.estimatedVsActual.variance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {metrics.estimatedVsActual.variance > 0 ? '+' : ''}{metrics.estimatedVsActual.variance}%
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedReports;
